import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { LabelWidthValues, MenuPosition } from '../../../types/common/types';
import { DateTimeFilterTranslateStrings, TimeIntervalPoints, TimeRangeEvents } from '../types';
import { WEB_COMPONENT_GZ_DATE_RANGE_PICKER, WEB_COMPONENT_GZ_DROPDOWN_MENU } from '../../../webcomponents.constants';
import { GzDropdownMenuWebComponent } from '../../gzDropdownMenu';
import { GzRadioGroupWebComponent } from '../../gzRadio';
import { TimeRangeKind, TimeRangeSchema } from '@nirvana/shared/dtos/types/shared/time';
import { GzDateRangePickerWebComponent } from '../../gzDateRangePicker';
import {
  All_TO_DATE_RANGE_VALUE,
  CUSTOM_TIME_RANGE_VALUE,
  DateRangePickerTranslateStrings,
} from '../../gzDateRangePicker/types';
import { getTime } from 'date-fns';
import {
  dayFormatter,
  getHourFromTimestamp,
  getMinuteFromTimestamp,
  jsTimestampToUnixTimestamp,
  unixTimestampToJsTimestamp,
} from '@nirvana/shared/time';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import type { TimeRangeOption } from '../types';
import type { AbsoluteTimeRange, TimeRange } from '@nirvana/shared/dtos/types/shared/time';
import { format as formatInTimezone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { getLocaleObjectFromUserLocale } from '../../../utils/i18n.utils';
import { getAbsoluteTimerangeFromRelativeInterval, getUTCTimestampsFromDateFilter } from '../../../utils/filters.utils';

@customElement('gz-datetime-filter')
export class GzDatetimeFilterWebComponent extends LitElement {
  @property({ type: String, reflect: true })
  label = '';

  @property({ type: String, reflect: true })
  placeholder = '';

  @property({ type: String, reflect: true })
  labelWidth = LabelWidthValues.LARGE;

  @property({ type: String, reflect: true })
  userLocale = '';

  @property({ type: String, reflect: true })
  timezone = '';

  /**
   * This property is used to store the strings displayed on the filter (i.e. buttons and input labels/placeholders).
   * The component doesn't directly translate the strings, these values need to be provided by the developer.
   */
  @property({ type: Object, reflect: true })
  translateStrings: DateTimeFilterTranslateStrings = {
    confirmButton: 'PLACEHOLDER_APPLY',
    cancelButton: 'PLACEHOLDER_CANCEL',
    fromLabel: 'PLACEHOLDER_FROM',
    toLabel: 'PLACEHOLDER_TO',
    customLabel: 'PLACEHOLDER_CUSTOM',
    allToDate: 'PLACEHOLDER_ALL_TO_DATE',
  };

  @property({ type: Array, reflect: true })
  timeRangeOptions: TimeRangeOption[] = [];

  /**
   * Max date allowed for selection. Format is Unix timestamp.
   * When max is set, the dates after the maximum timestamp will be disabled on the calendar.
   */
  @property({ type: Number })
  max;

  /**
   * Minimal date allowed for selection. Format is Unix timestamp.
   * When min is set, the dates before the minimum timestamp will be disabled on the calendar.
   */
  @property({ type: Number })
  min;

  /**
   * This property displays the 'All (to date)' quick selection radio button on the filter.
   */
  @property({ type: Boolean, reflect: true })
  enableSelectAllToDate = false;

  /**
   * This property should remain private, it's used for data binding between the radio buttons and the date picker
   * custom hours inputs. When set to TRUE, the hours inputs should switch state to `readonly`
   */
  @property({ type: Boolean, reflect: true, attribute: false })
  private isAllToDateSelected = false;

  /**
   * Setting this to true will display the read-only input for the selected date and number inputs for customizing the
   * hours and minutes in the filter. Also, the label will display the hours and minutes for the selected interval,
   * i.e. 9 Mar 2020, 08:00 - 7 Apr 2020, 12:45
   */
  @property({ type: Boolean })
  showTimePickers = false;

  @state()
  private menuPosition: MenuPosition;

  /**
   * This property will store the timeRange value on the component, as received from the backend. When this property
   * is updated with new values, we will save them on the activeTimeRange internal property and then use
   * activeTimeRange to manipulate the dateRange picker UI. If the user cancels the selection, we use the
   * 'selectedTimeRange' prop to revert the filter UI to the previous values.
   */
  @property({ type: Object, reflect: true })
  private selectedTimeRange: TimeRange | null;

  /**
   * This property will store a copy the currently active timeRange on the component.
   */
  @state()
  private activeTimeRange: TimeRange | null;

  /**
   * This property will store the computed timestamps from the relative or absolute timeRange. It's used for
   * updating the dateRange picker UI.
   */
  @state()
  private formattedTimeRange: AbsoluteTimeRange | null;

  @query(WEB_COMPONENT_GZ_DROPDOWN_MENU)
  private readonly dropdownMenu!: GzDropdownMenuWebComponent;

  @query('#timeRangeIntervalOptions')
  private readonly timeRangeIntervalOptions!: GzRadioGroupWebComponent;

  @query('#parent')
  private readonly parent!: HTMLElement;

  @query(WEB_COMPONENT_GZ_DATE_RANGE_PICKER)
  private dateRangePicker!: GzDateRangePickerWebComponent;

  static get styles() {
    return styles;
  }

  updated(properties: PropertyValues): void {
    if (properties.has('selectedTimeRange')) {
      this.activeTimeRange = this.selectedTimeRange;
      this.updateFilterUIFromTimeRange(this.activeTimeRange, true);
    }

    if (properties.has('labelWidth')) {
      this.dropdownMenu.labelWidth = this.labelWidth;
    }

    if (properties.has('userLocale')) {
      this.updateDropdownLabelWithTimeInterval(this.formattedTimeRange);
    }
  }

  private updateFilterUIFromTimeRange(timeRange: TimeRange | null, mustUpdateDropdownLabel = false) {
    if (!timeRange) {
      this.timeRangeIntervalOptions.removeAttribute('selectedOption');
      this.isAllToDateSelected = false;
    } else if (timeRange.kind === TimeRangeKind.RELATIVE_TIME_RANGE && timeRange.relative) {
      const { lastNumDays } = timeRange.relative;
      this.timeRangeIntervalOptions.selectedOption = lastNumDays;
    } else if (timeRange.kind === TimeRangeKind.ABSOLUTE_TIME_RANGE && timeRange.absolute) {
      this.timeRangeIntervalOptions.selectedOption = CUSTOM_TIME_RANGE_VALUE;
    } else {
      this.timeRangeIntervalOptions.selectedOption = All_TO_DATE_RANGE_VALUE;
    }

    this.formattedTimeRange = getUTCTimestampsFromDateFilter(timeRange, this.timezone);
    if (mustUpdateDropdownLabel) {
      this.updateDropdownLabelWithTimeInterval(this.formattedTimeRange);
    }

    this.updateTimeRangeOnDateRangePicker(this.formattedTimeRange);
  }

  private updateDropdownLabelWithTimeInterval(interval: AbsoluteTimeRange | null) {
    if (this.isCustomTimeRangeIntervalSelected(All_TO_DATE_RANGE_VALUE)) {
      this.dropdownMenu.selectedValuesStrings = this.translateStrings.allToDate;
    } else if (!interval || isEmpty(interval) || this.dropdownMenu.labelWidth === LabelWidthValues.SMALL) {
      this.dropdownMenu.selectedValuesStrings = '';
    } else {
      const format = this.showTimePickers ? 'd MMM yyyy HH:mm' : 'd MMM yyyy';

      // display times in the set timezone
      const from = interval.fromTimestamp
        ? formatInTimezone(utcToZonedTime(interval.fromTimestamp, this.timezone), format, {
            timeZone: this.timezone,
            locale: getLocaleObjectFromUserLocale(this.userLocale),
          })
        : undefined;
      const to = interval.toTimestamp
        ? formatInTimezone(utcToZonedTime(interval.toTimestamp, this.timezone), format, {
            timeZone: this.timezone,
            locale: getLocaleObjectFromUserLocale(this.userLocale),
          })
        : undefined;

      this.dropdownMenu.selectedValuesStrings = `${from}${to ? ' - ' + to : ''}`;
    }
  }

  render() {
    return html`
      <gz-dropdown-menu
        .label="${this.label}"
        .placeholder="${this.placeholder}"
        .labelWidth="${this.labelWidth}"
        .menuPosition="${this.menuPosition}"
        icon="ds-icon-calendar"
        @close="${this.cancelTimeRangeSelection}"
        @open="${this.handleDropdownMenuOpen}"
      >
        <div class="filter">
          <div class="filter--timerange-select">
            <gz-radio-group id="timeRangeIntervalOptions" @radioSelectionChanged="${this.handleTimerangeChange}">
              ${this.renderPresetTimeRangeOptions()} ${this.renderAllToDateOption()}
              <gz-radio .label="${this.translateStrings.customLabel}" value="${CUSTOM_TIME_RANGE_VALUE}"></gz-radio>
            </gz-radio-group>
          </div>

          <div class="filter--datepicker">
            <div class="filter--calendar" id="parent">
              <gz-date-range-picker
                ?showTimePickers="${this.showTimePickers}"
                .userLocale="${this.userLocale}"
                .timezone="${this.timezone}"
                .min="${this.min}"
                .max="${this.max}"
                .translateStrings=${{
                  confirmButton: this.translateStrings.confirmButton,
                  cancelButton: this.translateStrings.cancelButton,
                  fromLabel: this.translateStrings.fromLabel,
                  toLabel: this.translateStrings.toLabel,
                } as DateRangePickerTranslateStrings}
                ?isAllToDateSelected="${this.isAllToDateSelected}"
                @dateFromChanged="${this.selectCustomValueFromRadioGroup}"
                @selectedRangeConfirmed="${this.confirmTimeRangeSelection}"
                @selectedRangeCancelled="${this.cancelTimeRangeSelection}"
                @customHoursChanged="${this.selectCustomValueFromRadioGroup}"
              ></gz-date-range-picker>
            </div>
          </div>
        </div>
      </gz-dropdown-menu>
    `;
  }

  private renderAllToDateOption(): TemplateResult | undefined {
    if (!this.enableSelectAllToDate) {
      return;
    }
    return html`<gz-radio .label="${this.translateStrings.allToDate}" value="${All_TO_DATE_RANGE_VALUE}"></gz-radio> `;
  }

  private computeDropdownMenuPosition() {
    const windowWidth = window.innerWidth;
    const menuClientRect = this.getBoundingClientRect();
    const consoleMenu = document.querySelector('.mainMenu')?.clientWidth || 0;
    const dropdownMenuWidth = 800;

    // get available space between left side of the menu and right side of the window
    // center dropdown menu if there is no space on left or right of the dropdown
    const availableSpaceRight = windowWidth - menuClientRect.left;
    if (availableSpaceRight < dropdownMenuWidth && menuClientRect.right - consoleMenu < dropdownMenuWidth) {
      const menuTranslateValue = dropdownMenuWidth / 2 - menuClientRect.width / 2;
      this.menuPosition = MenuPosition.CENTER;
      this.dropdownMenu.centerMenuPositionHorizontally(menuTranslateValue);
      return;
    }
    this.menuPosition = availableSpaceRight < dropdownMenuWidth ? MenuPosition.RIGHT : MenuPosition.LEFT;
  }

  private updateTimeRangeOnDateRangePicker(timeRange: AbsoluteTimeRange | null) {
    if (!timeRange) {
      this.dateRangePicker.clearSelectedInterval();
      return;
    }
    if (!timeRange.fromTimestamp || !timeRange.toTimestamp) {
      return;
    }

    // display times in the set timezone
    const fromTimestamp = utcToZonedTime(timeRange.fromTimestamp, this.timezone).valueOf();
    const toTimestamp = utcToZonedTime(timeRange.toTimestamp, this.timezone).valueOf();

    this.updatePickerInputsValues(jsTimestampToUnixTimestamp(fromTimestamp), TimeIntervalPoints.FROM);
    this.updatePickerInputsValues(jsTimestampToUnixTimestamp(toTimestamp), TimeIntervalPoints.TO);
  }

  private updatePickerInputsValues(timestamp: number, intervalPoint: TimeIntervalPoints) {
    const day = timestamp
      ? dayFormatter(timestamp * 1000, this.timezone, getLocaleObjectFromUserLocale(this.userLocale))
      : '';
    const hours = getHourFromTimestamp(timestamp * 1000, this.timezone);
    const minutes = getMinuteFromTimestamp(timestamp * 1000, this.timezone);
    this.dateRangePicker.updateTimeRangeInputs(intervalPoint, { day, hours, minutes, seconds: 0 });
    if (!this.isCustomTimeRangeIntervalSelected(All_TO_DATE_RANGE_VALUE)) {
      this.dateRangePicker[intervalPoint] = timestamp;
    }
  }

  private handleTimerangeChange(e: CustomEvent) {
    const selectedTimerange = e.detail.selectedValue;
    this.isAllToDateSelected = this.isCustomTimeRangeIntervalSelected(All_TO_DATE_RANGE_VALUE);

    if (this.isCustomTimeRangeIntervalSelected(All_TO_DATE_RANGE_VALUE)) {
      this.dateRangePicker.isRelativeTimeRange = false;
      this.activeTimeRange = null;
      this.updateFilterUIwithAllToDate();
      return;
    }

    if (this.isCustomTimeRangeIntervalSelected(CUSTOM_TIME_RANGE_VALUE)) {
      this.dateRangePicker.submitDisabled = isEmpty(this.dateRangePicker.fromTimestamp);
      this.dateRangePicker.isRelativeTimeRange = false;

      if (isEmpty(this.dateRangePicker.fromTimestamp) || isEmpty(this.dateRangePicker.toTimestamp)) {
        this.dateRangePicker.submitDisabled = true;
      }
      return;
    }

    this.dateRangePicker.submitDisabled = false;
    this.dateRangePicker.isRelativeTimeRange = true;
    this.dateRangePicker.resetDisplayedMonths();

    const interval = getAbsoluteTimerangeFromRelativeInterval(selectedTimerange, this.timezone);
    this.updateTimeRangeOnDateRangePicker(interval);
  }

  private renderPresetTimeRangeOptions(): TemplateResult | undefined {
    if (isEmpty(this.timeRangeOptions)) {
      return;
    }
    return html`${this.timeRangeOptions.map(option => this.renderTimeRangeOption(option))}`;
  }

  private renderTimeRangeOption(option: TimeRangeOption) {
    return html` <gz-radio .label="${option.label}" .value="${option.value}"></gz-radio>`;
  }

  /**
   * Emit the calendar selection event and close the menu
   */
  private confirmTimeRangeSelection(e: CustomEvent) {
    const { selectedOption } = this.timeRangeIntervalOptions;
    const customTimerange: AbsoluteTimeRange | undefined = e.detail;

    this.activeTimeRange = this.getTimeRangeFromRadioGroupSelection(selectedOption, customTimerange);
    this.selectedTimeRange = this.activeTimeRange;

    if (this.isCustomTimeRangeIntervalSelected(All_TO_DATE_RANGE_VALUE)) {
      this.dispatchEvent(
        new CustomEvent(TimeRangeEvents.ALL_TO_DATE, {
          detail: { timeRange: { kind: TimeRangeKind.ALL_TO_DATE } },
          bubbles: true,
          composed: true,
        })
      );
      this.dropdownMenu.label = this.label;
      this.dropdownMenu.selectedValuesStrings = this.translateStrings.allToDate;
    } else {
      this.handleSelectionEvent(this.activeTimeRange);
    }

    this.dropdownMenu.open = false;
  }

  /**
   * Reset the filter UI state and close the dropdown
   */
  private cancelTimeRangeSelection() {
    this.activeTimeRange = this.selectedTimeRange;
    this.updateFilterUIFromTimeRange(this.activeTimeRange);
    this.dropdownMenu.open = false;
  }

  private getTimeRangeFromRadioGroupSelection(
    selectedOption: string | number,
    customTimerange?: AbsoluteTimeRange
  ): TimeRange {
    if (selectedOption === CUSTOM_TIME_RANGE_VALUE && !isEmpty(customTimerange)) {
      const fromTimestamp = unixTimestampToJsTimestamp(customTimerange?.fromTimestamp as number);
      const toTimestamp = unixTimestampToJsTimestamp(customTimerange?.toTimestamp as number);
      return {
        kind: TimeRangeKind.ABSOLUTE_TIME_RANGE,
        absolute: {
          // inputs are on the set timezone and need to be restored to UTC
          fromTimestamp: zonedTimeToUtc(new Date(fromTimestamp), this.timezone).valueOf(),
          toTimestamp: zonedTimeToUtc(new Date(toTimestamp), this.timezone).valueOf(),
        },
      };
    }
    if (selectedOption === All_TO_DATE_RANGE_VALUE) {
      return {
        kind: TimeRangeKind.ABSOLUTE_TIME_RANGE,
      };
    }

    return {
      kind: TimeRangeKind.RELATIVE_TIME_RANGE,
      relative: { lastNumDays: parseInt(selectedOption as string, 10) },
    };
  }

  private handleSelectionEvent(timerange: TimeRange) {
    // working with UTC timestamps
    const event = new CustomEvent(TimeRangeEvents.TIMERANGE_CHANGE, {
      detail: { timeRange: timerange },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  private selectCustomValueFromRadioGroup() {
    this.dateRangePicker.isRelativeTimeRange = false;
    this.timeRangeIntervalOptions.selectOption(CUSTOM_TIME_RANGE_VALUE);
  }

  public setSelectedTimeRange(selectedTimeRange: TimeRange | null) {
    if (selectedTimeRange) {
      selectedTimeRange = TimeRangeSchema.parse(selectedTimeRange);
    }
    this.selectedTimeRange = selectedTimeRange;
  }

  public handleDropdownMenuOpen() {
    this.computeDropdownMenuPosition();
    this.dateRangePicker.resetDisplayedMonths();

    if (!this.activeTimeRange) {
      return;
    }

    if (this.isCustomTimeRangeIntervalSelected(All_TO_DATE_RANGE_VALUE)) {
      this.updateFilterUIwithAllToDate();
      return;
    }
    this.updateFilterUIFromTimeRange(this.activeTimeRange);
  }

  private isCustomTimeRangeIntervalSelected(radioButtonValue: string): boolean {
    return this.timeRangeIntervalOptions.selectedOption === radioButtonValue;
  }

  private updateFilterUIwithAllToDate() {
    const now = jsTimestampToUnixTimestamp(getTime(utcToZonedTime(Date.now(), this.timezone)));

    this.dateRangePicker.clearSelectedInterval();
    this.updatePickerInputsValues(now, TimeIntervalPoints.TO);
    this.dateRangePicker.submitDisabled = false;
  }
}
